import React, {useCallback, useEffect, useState} from "react";
import {useApolloClient, useMutation, useQuery} from "react-apollo";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useLocalStorage, VisuallyHidden} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import {useSocketListener} from "@unibuddy/sockets";

import PageHeader from "shared/Shared/components/PageHeader/PageHeader";
import {setMe} from "shared/Auth/actions/authActions";
import {rootURL} from "shared/Utils/WhereAmI";
import {ErrorDisplay} from "shared/General/components/ErrorDisplay/ErrorDisplay";
import {PhoneNumberToast} from "shared/Chat/components/PhoneNumberToast/PhoneNumberToast";
import checkAllowDelinkedForApplicant from "shared/Chat/utils/checkAllowDelinkedForApplicant";
import Queue, {QueueItem} from "shared/General/components/Queue/Queue";
import {
    pageNames,
    popupNames,
    sharedEvents,
} from "shared/AnalyticsNew/constants/productSpecification";

import {PageTitle} from "../../../../../components/PageTitle/PageTitle";
import DelinkedMessage from "../../components/DelinkedMessageBox/DelinkedMessage";
import enableSMSMutation from "../../mutations/enableSMSMutation";
import LoadingChat from "../../components/LoadingChat/LoadingChat";
import s from "../Chat/Chat.pcss";
import {StyledChatComponent} from "../common/StyledChatComponent";
import {Chat as ChatV2} from "../../components/Chat/Chat";
import {PopupQuestionWrapper} from "../../components/PopupQuestions/PopupQuestionWrapper";
import {IntlNamespaces} from "../../../../../translations/types";
import {
    INTL_ADD_PHONE_NUMBER_MESSAGE,
    INTL_CONVERSATION_WITH,
    INTL_NOTIFICATION_MESSAGE,
    INTL_REPLY_LATER,
} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";
import {INTL_INBOX} from "../../../../../translations/inbox";
import {INTL_BACK} from "../../../../../translations/common";
import useAnalytics from "../../../../../shared/AnalyticsNew/hooks/useAnalytics";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import useAuth from "../../../../../shared/Auth/hooks/useAuth";
import {useEmbeddedEvents} from "../../hooks/useEmbeddedEvents";
import sharedEmbeddedEvents from "../../../../../shared/EmbeddedEvents/constants/EmbeddedEvents";
import useErrorReporting from "../../../../../shared/General/components/ErrorReporting/useErrorReporting";
import {useHasUniversityAppliedStatus} from "../../hooks/useHasUniversityAppliedStatus";
import {PopupQuestions} from "../../components/PopupQuestions/PopupQuestionsV2";
import messagesQuery from "../../../../../shared/Chat/queries/messagesQuery";
import recordActivityMutation from "../../../../../shared/Activity/mutations/recordActivityMutation";
import sendMessageMutation, {
    optimisticResponse,
} from "../../../../../shared/Chat/mutations/sendMessageMutation";
import {getReduxDispatch} from "../../../../../App";
import {chatMessageEvent} from "../../../../modules/SocketWrapperProvider/Sockets";
import writeMessageToApolloCache from "../../../../../shared/Chat/functions/writeMessageToApolloCache";
import {MessageField} from "../../../../../types/generated";

export function Chat() {
    const {trackEvent} = useAnalytics();
    const history = useHistory();
    const {authState} = useAuth();
    const postEmbeddedMessage = useEmbeddedEvents();
    const {chatGroupId} = useParams<{chatGroupId: string}>();
    const university = useUniversity();
    const [savePhoneNumber] = useMutation(enableSMSMutation);
    const [sendMessage] = useMutation(sendMessageMutation);
    const [recordActivity] = useMutation(recordActivityMutation);
    const client = useApolloClient();
    const dispatch = getReduxDispatch();

    const [phonePopupDismissed, setPhonePopupDismissed] = useLocalStorage(
        `PHONE_POPUP_DISMISSED_${chatGroupId}`,
        false,
    );
    const [updatingPhoneNumber, setUpdatingPhoneNumber] = useState<boolean>(false);
    const {slug: universitySlug, features} = university;
    const isDelinked = checkAllowDelinkedForApplicant(universitySlug, authState?.me);
    const allowDelinked = checkAllowDelinkedForApplicant(universitySlug, authState?.me);
    const isDegreeTypesEnabled = features.get("enableDegreeTypes");
    const {
        data: messagesData,
        loading: messagesLoading,
        error: messagesError,
    } = useQuery(messagesQuery, {
        variables: {
            chatGroupId,
            allowDelinked,
            isDegreeTypesEnabled,
        },
        fetchPolicy: "cache-and-network",
    });
    const messages = messagesData?.messages?.messages ?? [];
    const chatGroup = messagesData?.messages?.chatGroup ?? {};
    const opponentName = chatGroup?.opponent?.firstName ?? "Buddy";
    const {pathname, search} = useLocation();
    const {reportError} = useErrorReporting();
    const {t} = useTranslation([IntlNamespaces.CHAT, IntlNamespaces.INBOX, IntlNamespaces.COMMON]);
    const showAppliedToUniversityQuestion = !features.get("hideAppliedToUniversityQuestion");
    const hasUniversityAppliedStatus = useHasUniversityAppliedStatus();
    const showAddPhoneNumber = authState?.me?.applicant?.phoneNumber === null;
    const phoneNumberText = showAddPhoneNumber
        ? t(INTL_ADD_PHONE_NUMBER_MESSAGE.key, INTL_ADD_PHONE_NUMBER_MESSAGE.defaultValue)
        : t(INTL_NOTIFICATION_MESSAGE.key, INTL_NOTIFICATION_MESSAGE.defaultValue);

    const onSend = async ({text}: {text: string}) => {
        if (text === "") return;

        const cacheVariables = {
            chatGroupId,
            allowDelinked,
            isDegreeTypesEnabled,
        };

        const variables = {
            text,
            chatGroupId,
            skipNotifications: false,
            allowDelinked,
        };

        const optimisticResponseObject = optimisticResponse({
            text,
            chatGroupId,
            created: new Date().toISOString(),
            sender: authState?.me?.anyUser,
            richContent: false,
        });

        const optimisticMessage = optimisticResponseObject?.sendMessage?.message;

        try {
            const {messages: messagesCache} = client.readQuery({
                query: messagesQuery,
                variables: cacheVariables,
            });
            if (messagesCache) {
                client.writeQuery({
                    query: messagesQuery,
                    variables: cacheVariables,
                    data: {
                        messages: {
                            messages: [...messagesCache.messages, optimisticMessage],
                            chatGroup: messagesCache.chatGroup,
                            hasMore: messagesCache.hasMore,
                            __typename: "ChatGroupAndMessages",
                        },
                    },
                });
            }

            const {data} = await sendMessage({variables});

            if (data?.sendMessage?.message) {
                const {messages: updatedMessagesCache} = client.readQuery({
                    query: messagesQuery,
                    variables: cacheVariables,
                });

                if (updatedMessagesCache) {
                    const newMessages = [...updatedMessagesCache.messages];
                    newMessages.pop();
                    newMessages.push(data.sendMessage.message);

                    client.writeQuery({
                        query: messagesQuery,
                        variables: cacheVariables,
                        data: {
                            messages: {
                                messages: newMessages,
                                chatGroup: updatedMessagesCache.chatGroup,
                                hasMore: updatedMessagesCache?.hasMore ?? false,
                                __typename: "ChatGroupAndMessages",
                            },
                        },
                    });
                }
            }

            let opponentType = chatGroup?.opponent?.accountRole;
            if (opponentType === "mentor" && chatGroup?.opponent?.degreeLevel === "Alumni") {
                opponentType = "alumni";
            }
            trackEvent(sharedEvents.messageSent.name, {
                conversationId: chatGroup?.id ?? "",
                opponentId: chatGroup?.opponent?.id ?? "",
                opponentType,
                chatVersion: "Version 2",
            });

            const isEmbedded = window.parent !== window;

            try {
                if (isEmbedded) {
                    postEmbeddedMessage(sharedEmbeddedEvents.messageSent.name, {});
                }
            } catch (error) {
                console.error("Error sending embedded message:", error);
            }
        } catch (error) {
            reportError(error);
        }
    };

    const onAmbassadorNewMessage = useCallback(
        (message: MessageField) => {
            const user = authState?.me?.anyUser;
            if (user && message?.sender?.id === user.id) return;

            try {
                const cacheVariables = {
                    chatGroupId,
                    allowDelinked,
                    isDegreeTypesEnabled,
                };
                writeMessageToApolloCache(message, client, cacheVariables);
            } catch (e) {
                console.error("Error writing message to cache ", e);
            }
        },
        [allowDelinked, authState, chatGroupId, client, isDegreeTypesEnabled],
    );

    useSocketListener({
        event: chatMessageEvent,
        channelId: chatGroupId,
        onEvent: onAmbassadorNewMessage,
    });

    const onAddPhoneNumber = async (phoneNumber: string, service: string) => {
        setUpdatingPhoneNumber(true);
        try {
            const {data} = await savePhoneNumber({
                variables: {
                    phoneNumber,
                    chatGroupId,
                    applicant: authState?.me?.anyUser?.id,
                    mobileAdded: true,
                    newMessageNotifications: ["email", service],
                },
            });

            if (data?.updateApplicant?.error) {
                setUpdatingPhoneNumber(false);
                reportError(data?.updateApplicant?.error);
                return false;
            }
            dispatch(setMe(data?.updateApplicant?.me));
            dispatch(setMe(data?.updateApplicantPreferences?.me));
            setUpdatingPhoneNumber(false);
            return true;
        } catch (error) {
            reportError(error);
            return false;
        }
    };

    const shouldShowWillReply = () => {
        // only display this if the opponent is a mentor
        if ((chatGroup?.opponent?.accountRole ?? "") === "mentor") {
            // do not show reply scenarios:
            // 1. Ambassador/uni replied
            // 2. Dismissed
            // 3. Phone number added
            const replyReceived =
                messages.filter(
                    (message: MessageField) => message?.sender?.accountRole !== "applicant",
                ).length > 1;
            const mobileAdded = authState?.me?.applicant?.phoneNumber;
            return !replyReceived && !phonePopupDismissed && !mobileAdded;
        }
        return false;
    };

    const errorDisplayBackFunction = () => {
        trackEvent(sharedEvents.clickedBackInChat.name, {
            universitySlug,
        });
        history.push(`${rootURL(pathname)}/inbox${search}`);
    };

    const redirectToRelink = () => {
        // redirecting to 2nd stage signup on settings tab
        history.replace(pathname, {from: "delinked"});
    };

    const queue = (
        <Queue>
            {showAppliedToUniversityQuestion && (
                <QueueItem enabled={!hasUniversityAppliedStatus}>
                    {({isOpen, onDismiss}) => {
                        return <PopupQuestions onDismiss={onDismiss} isOpen={isOpen} />;
                    }}
                </QueueItem>
            )}
            <QueueItem enabled={shouldShowWillReply()}>
                {({isOpen, onDismiss}) => {
                    const onDismissModal = () => {
                        if (showAddPhoneNumber) {
                            trackEvent(sharedEvents.phoneNumberCollectionDismissed.name, {
                                universitySlug,
                                popup: popupNames.phoneNumberCollection,
                                cta: "Close Button",
                            });
                        }
                        onDismiss();
                        setPhonePopupDismissed(true);
                    };
                    return (
                        <PopupQuestionWrapper onDismiss={onDismissModal} isOpen={isOpen}>
                            <PhoneNumberToast
                                isOpen={isOpen}
                                title={t(
                                    INTL_REPLY_LATER.key,
                                    isFunction(INTL_REPLY_LATER.defaultValue)
                                        ? INTL_REPLY_LATER.defaultValue(name)
                                        : INTL_REPLY_LATER.defaultValue,

                                    {
                                        name: opponentName,
                                    },
                                )}
                                text={phoneNumberText}
                                onAddPhoneNumber={onAddPhoneNumber}
                                updatingPhoneNumber={updatingPhoneNumber}
                                showAddPhoneNumber={showAddPhoneNumber}
                            />
                        </PopupQuestionWrapper>
                    );
                }}
            </QueueItem>
        </Queue>
    );

    useEffect(() => {
        trackEvent(sharedEvents.chatPageViewed.name, {prefix: pageNames.chat, universitySlug});
        // Empty deps array as we only want this to fire once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const recordActivityFunction = async () => {
            try {
                await recordActivity({
                    variables: {
                        app: "onix",
                        activity: "Chat",
                        activitySource: JSON.stringify({slug: universitySlug, type: "university"}),
                    },
                });
            } catch (error) {
                reportError(error);
            }
        };

        recordActivityFunction();

        // Empty deps array as we only want this to fire once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (messages?.length < 1 || messagesLoading) {
        return (
            <div id="chat-page">
                <PageTitle
                    title={t(
                        `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                        isFunction(INTL_CONVERSATION_WITH.defaultValue)
                            ? INTL_CONVERSATION_WITH.defaultValue(name)
                            : INTL_CONVERSATION_WITH.defaultValue,
                        {name: opponentName},
                    )}
                />
                <VisuallyHidden>
                    <PageHeader
                        title={t(
                            `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                            INTL_INBOX.defaultValue,
                        )}
                    />
                </VisuallyHidden>
                <div className={s.container}>
                    <StyledChatComponent className={s.chat}>
                        <LoadingChat />
                    </StyledChatComponent>
                </div>
            </div>
        );
    }

    return (
        <ErrorDisplay
            // TODO: Do we want to show error display when sending a message fails? If not, how should we handle it?
            error={messagesError}
            actionButton={{
                text: t(`${IntlNamespaces.COMMON}:${INTL_BACK.key}`, INTL_BACK.defaultValue),
                onClick: () => errorDisplayBackFunction(),
            }}
        >
            <div id="chat-page">
                <PageTitle
                    title={t(
                        `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                        isFunction(INTL_CONVERSATION_WITH.defaultValue)
                            ? INTL_CONVERSATION_WITH.defaultValue(name)
                            : INTL_CONVERSATION_WITH.defaultValue,
                        {name: opponentName},
                    )}
                />
                <VisuallyHidden>
                    <PageHeader
                        title={t(
                            `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                            INTL_INBOX.defaultValue,
                        )}
                    />
                </VisuallyHidden>
                <div className={s.container}>
                    <StyledChatComponent className={s.chat}>
                        <ChatV2
                            messages={messages}
                            user={authState?.me?.anyUser}
                            chatGroup={chatGroup}
                            chatFooterHidden={isDelinked}
                            chatPopup={
                                isDelinked ? (
                                    <DelinkedMessage redirectToRelink={redirectToRelink} />
                                ) : null
                            }
                            university={university}
                            onSend={onSend}
                            queue={queue}
                        />
                    </StyledChatComponent>
                </div>
            </div>
        </ErrorDisplay>
    );
}
